import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { createRealtimeChannel, cleanupChannel } from '@/utils/realtime-factory';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  link: string | null;
  created_at: string;
}

interface FetchOptions {
  page?: number;
  pageSize?: number;
  type?: string;
  readStatus?: 'all' | 'read' | 'unread';
}

export const useNotifications = (options: FetchOptions = {}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<RealtimeChannel | null>(null);
  
  const userId = user?.id;
  const { page = 1, pageSize = 10, type = 'all', readStatus = 'all' } = options;

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    try {
      // Get unread count globally for the user
      const { count: unread } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('read', false);
      
      setUnreadCount(unread || 0);

      // Main query with filters and pagination
      let query = supabase
        .from('notifications')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (type !== 'all') {
        query = query.eq('type', type);
      }
      
      if (readStatus === 'read') {
        query = query.eq('read', true);
      } else if (readStatus === 'unread') {
        query = query.eq('read', false);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, count, error } = await query;

      if (error) throw error;

      setNotifications(data as Notification[]);
      setTotalCount(count || 0);
    } catch (error) {
      console.error(`[Notifications] Error fetching:`, error);
    } finally {
      setLoading(false);
    }
  }, [userId, page, pageSize, type, readStatus]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!userId) return;

    if (channelRef.current) {
      cleanupChannel(channelRef.current);
    }

    const channel = createRealtimeChannel(`notifications:${userId}`);
    channelRef.current = channel;

    channel
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` }, () => {
        fetchNotifications();
      })
      .subscribe();

    return () => {
      cleanupChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [userId, fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;
      // Removed refetch here to avoid jumping or jumping while in a filtered list
      // The optimistic update handles the UI, and the readStatus filter if applied
      // might hide it on next fetch or manual refresh.
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;
      setUnreadCount(0);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [userId, fetchNotifications]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
      toast({ title: "Notificación eliminada" });
      fetchNotifications(); // Need to fetch to adjust counts and potentially fill pagination gap
    } catch (error) {
      console.error('Error deleting:', error);
    }
  }, [fetchNotifications]);

  const clearAll = useCallback(async () => {
    if (!userId) return;
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
      fetchNotifications();
    } catch (error) {
      console.error('Error clearing:', error);
    }
  }, [userId, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    totalCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refetch: fetchNotifications,
  };
};
